// Behavior for the sticky filter/sort bar rendered above .product-grid-container.
// Loaded as a module, so it only ever runs once per URL even if the snippet
// that includes it is rendered more than once on the same page.
//
// Nothing here edits the shared Alpine x-data/directives in the collection section
// markup - the default (non sticky-bar) filter/sort flow is untouched. This only
// reads/writes Alpine's existing reactive state from the outside, and only runs
// when a shopper interacts with the sticky bar's own buttons.

const PAGINATION_HIDE_STYLE = `
  :host([data-pagination-hidden]) .swiper-pagination,
  :host([data-pagination-hidden]) .swiper-button-next,
  :host([data-pagination-hidden]) .swiper-button-prev {
    display: none !important;
  }
`;

// swiper-container renders pagination/navigation inside a shadow root, so a plain
// stylesheet selector in an external file can never reach it. Injecting the override
// straight into that shadow root - once per instance - and flipping a host attribute
// is instant (no rAF/update() wait) and survives Swiper's own re-renders, since a
// shadow-scoped !important rule always outranks the non-important inline style
// Swiper sets on its pagination/nav elements.
function setSwiperPaginationHidden(el, hidden) {
  const root = el.shadowRoot;
  if (!root) return;

  if (!root.querySelector('style[data-sticky-filter-bar-pagination]')) {
    const style = document.createElement('style');
    style.setAttribute('data-sticky-filter-bar-pagination', '');
    style.textContent = PAGINATION_HIDE_STYLE;
    root.appendChild(style);
  }

  el.toggleAttribute('data-pagination-hidden', hidden);
}

// Swipers in the main grid plus any featured collection following the same view
// (.plp-grid-follower, see setGridView).
function getViewSwipers(gridContainer) {
  return [
    ...(gridContainer?.querySelectorAll('swiper-container') || []),
    ...document.querySelectorAll('.plp-grid-follower swiper-container'),
  ];
}

function updateSwiperPagination(gridContainer) {
  // The 9-icon view is "image only" on every breakpoint, not just mobile.
  const shouldHide = gridContainer?.getAttribute('data-grid-view') === '9';
  getViewSwipers(gridContainer).forEach((el) => {
    setSwiperPaginationHidden(el, shouldHide);
  });
}

function refreshSwipers(gridContainer) {
  // Pagination visibility has no layout dependency, so it can flip immediately.
  updateSwiperPagination(gridContainer);

  // Slide geometry does depend on the grid's new column width, so give the browser
  // a couple of frames to commit the grid-template-columns change before Swiper
  // re-measures it.
  requestAnimationFrame(() => requestAnimationFrame(() => {
    getViewSwipers(gridContainer).forEach((el) => {
      el.swiper?.update();
    });
  }));
}

// Applies a grid view (or clears it with null). It's mirrored onto <html> as
// data-plp-grid-view so sections outside .product-grid-container - e.g. the "Shop Related"
// featured collection (sections/section-collection.liquid) - can follow the same view.
function setGridView(gridContainer, view) {
  if (view) {
    gridContainer?.setAttribute('data-grid-view', view);
    document.documentElement.setAttribute('data-plp-grid-view', view);
  } else {
    gridContainer?.removeAttribute('data-grid-view');
    document.documentElement.removeAttribute('data-plp-grid-view');
  }
}

// Active view state: the class drives the styling, aria-pressed tells screen readers.
function setViewButtonActive(button, active) {
  if (!button) return;
  button.classList.toggle('is-active', active);
  button.setAttribute('aria-pressed', String(active));
}

// Collection interrupters (page 1 only) take up grid cells alongside the products, so they
// count towards filling the rows: block = 1 cell, 50% = 2 (span 2), 100% = 4 (grid-column 1/5).
function countInterrupterCells(grid) {
  return [...grid.querySelectorAll('.collection-item-dynamic-interrupter')].reduce((cells, el) => {
    if (el.classList.contains('dynamic-interrupter-full-width')) return cells + 4;
    if (el.matches('.dynamic-interrupter-first-half-width, .dynamic-interrupter-last-half-width')) return cells + 2;
    return cells + 1;
  }, 0);
}

// The 9-icon view is 6 columns on desktop (3 on mobile), so it's only offered when the
// filtered product total is a multiple of 6 - otherwise the last row ends in empty slots.
// The total lives on .product-grid (inside the AJAX-swapped #product-grid-{id}), so this
// re-reads it after every filter/In Stock update. If the shopper was on the 9-icon view
// when it gets hidden, fall back to the next view down (4-icon).
function syncSixView(bar, gridContainer) {
  const sixButton = bar.querySelector('.sticky-filter-bar__view-btn[data-grid-view="9"]');
  const countEl = gridContainer?.querySelector('[data-products-count]');
  if (!sixButton || !countEl) return;

  const count = (parseInt(countEl.dataset.productsCount, 10) || 0) + countInterrupterCells(countEl);
  const hide = count === 0 || count % 6 !== 0;
  sixButton.hidden = hide;

  if (hide && sixButton.classList.contains('is-active')) {
    const fallbackButton = bar.querySelector('.sticky-filter-bar__view-btn[data-grid-view="4"]');
    setViewButtonActive(sixButton, false);
    setViewButtonActive(fallbackButton, true);
    setGridView(gridContainer, '4');
    refreshSwipers(gridContainer);
  }
}

// The same trigger button opens the whole filter+sort panel; the two variants of
// the underlying UI flag "open" with a different class on a different ancestor.
function getDrawerState(wrapper) {
  const trigger = wrapper.querySelector('.drawer__facet-button-trigger');
  if (!trigger) return null;

  const sortingWrapper = trigger.closest('.sorting-wrapper');
  const drawerRoot = trigger.closest('.drawer-main-wrapper, .drawer-main-col');
  const alpineTarget = drawerRoot || sortingWrapper;

  return {
    trigger,
    isOpen: Boolean(
      drawerRoot?.classList.contains('drawer-active')
      || sortingWrapper?.classList.contains('mobile-filter-active')
    ),
    setOpen(open) {
      const data = alpineTarget && window.Alpine?.$data(alpineTarget);

      if (data && 'open' in data) {
        data.open = open;
      } else if (data && 'openMobileFilter' in data) {
        data.openMobileFilter = open;
      } else {
        trigger.click();
      }
    },
  };
}

// On collections that render a category carousel/breadcrumb (sections/cat-carousel.liquid),
// that block isn't sticky itself - it just scrolls away normally, above the sticky bar in
// the DOM. If it's still partially on screen when the sticky bar's own top:header-height
// would otherwise kick in, the bar would stick right under the header and sit on top of it.
// Following its bottom edge (falling back to the header height once it scrolls past) keeps
// the bar pinned just below whichever of the two is still visible.
function initStickyOffset(bar) {
  const catCarousel = document.querySelector('.cat-carousel');
  if (!catCarousel) return;

  let ticking = false;

  const update = () => {
    ticking = false;
    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const catCarouselBottom = catCarousel.getBoundingClientRect().bottom;
    bar.style.top = `${Math.max(headerHeight, catCarouselBottom)}px`;
  };

  const onScrollOrResize = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  update();
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);
}

function ensureDrawerOpen(wrapper) {
  const state = getDrawerState(wrapper);
  if (state && !state.isOpen) state.setOpen(true);
}

function openSortControl(wrapper) {
  const nativeSelect = wrapper.querySelector('select.facet-filters__sort');
  if (nativeSelect && nativeSelect.offsetParent !== null) {
    nativeSelect.focus();
    if (typeof nativeSelect.showPicker === 'function') {
      try {
        nativeSelect.showPicker();
      } catch (error) {
        // Some browsers only allow showPicker() from a direct user gesture on the
        // select itself; focusing it is still a reasonable fallback.
      }
    }
    return;
  }

  const sortToggle = wrapper.querySelector('[id^="sort-by-drawer-"]');
  const sortList = sortToggle?.querySelector('.facet-filters__sort-list');
  if (sortToggle && sortList && sortList.style.display === 'none') {
    const data = window.Alpine?.$data(sortToggle);
    if (data && 'open' in data) {
      data.open = true;
    } else {
      sortToggle.click();
    }
  }
}

function initStickyFilterBar(bar) {
  const wrapper = bar.closest('.collection-filters-wrapper');
  if (!wrapper) return;

  initStickyOffset(bar);

  const viewButtons = bar.querySelectorAll('.sticky-filter-bar__view-btn');
  const gridContainer = bar.closest('.product-grid-container');

  const defaultActiveButton = bar.querySelector('.sticky-filter-bar__view-btn.is-active');
  if (defaultActiveButton) {
    setGridView(gridContainer, defaultActiveButton.dataset.gridView);
  }

  syncSixView(bar, gridContainer);

  viewButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const view = button.dataset.gridView;
      const isActive = button.classList.contains('is-active');

      viewButtons.forEach((btn) => setViewButtonActive(btn, false));

      if (isActive) {
        setGridView(gridContainer, null);
      } else {
        setViewButtonActive(button, true);
        setGridView(gridContainer, view);
      }

      refreshSwipers(gridContainer);
    });
  });

  const filterButton = bar.querySelector('[data-sticky-action="filter"]');
  filterButton?.addEventListener('click', (event) => {
    // .sorting-wrapper has @click.outside="openMobileFilter = false" listening on
    // document. This button lives outside .sorting-wrapper, so without stopping
    // propagation here, the very click that opens the drawer also bubbles up and
    // gets read as an "outside click", immediately closing it again.
    event.stopPropagation();

    // The drawer's own state (Alpine's open/openMobileFilter) is the source of
    // truth here, not our filters-panel-collapsed class - it can change on its
    // own (e.g. the drawer's internal close button), and if we tracked our own
    // class instead, the two would drift out of sync after that happened.
    const state = getDrawerState(wrapper);
    const isOpen = state ? state.isOpen : !wrapper.classList.contains('filters-panel-collapsed');

    if (isOpen) {
      wrapper.classList.add('filters-panel-collapsed');
      state?.setOpen(false);
    } else {
      wrapper.classList.remove('filters-panel-collapsed');
      state?.setOpen(true);
    }
  });

  const sortButton = bar.querySelector('[data-sticky-action="sort"]');
  sortButton?.addEventListener('click', (event) => {
    event.stopPropagation();
    wrapper.classList.remove('filters-panel-collapsed');
    ensureDrawerOpen(wrapper);
    openSortControl(wrapper);
  });

  // The grid gets re-fetched via AJAX on filter/sort changes; re-apply the pagination
  // visibility state to whatever swiper-container instances just got rendered in.
  // Also re-check whether the 9-icon view still fills its rows for the new product total.
  document.addEventListener('malbon:collection-updated', () => {
    syncSixView(bar, gridContainer);
    refreshSwipers(gridContainer);
  });

  // Infinite scroll / Load more inserts cards straight into the grid without an event,
  // so watch for new swiper-containers and give them the current view's pagination state.
  if (gridContainer) {
    let pending = false;
    new MutationObserver((mutations) => {
      if (pending) return;
      const addedSwiper = mutations.some((mutation) =>
        [...mutation.addedNodes].some(
          (node) => node.nodeType === 1 && (node.matches('swiper-container') || node.querySelector('swiper-container'))
        )
      );
      if (!addedSwiper) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        updateSwiperPagination(gridContainer);
      });
    }).observe(gridContainer, { childList: true, subtree: true });
  }
}

document.querySelectorAll('.sticky-filter-bar').forEach(initStickyFilterBar);
